import { useEffect, useState } from 'react';

interface Props {
    isOpen: boolean;
    imageUrl: string | null;
    fileName?: string;
    onClose: () => void;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export default function ImageAttachmentPreviewModal({ isOpen, imageUrl, fileName, onClose }: Props) {
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        if (!isOpen) {
            setZoom(1);
            return;
        }

        setZoom(1);
    }, [isOpen, imageUrl]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();

            if (key === 'escape') {
                onClose();
                return;
            }

            if ((event.ctrlKey || event.metaKey) && ['+', '=', '-', '0'].includes(event.key)) {
                event.preventDefault();
            }

            if (event.key === '+' || event.key === '=') {
                setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
                return;
            }

            if (event.key === '-') {
                setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
                return;
            }

            if (key === '0') {
                setZoom(1);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const handleWheel = (event: WheelEvent) => {
            event.preventDefault();

            setZoom((current) => {
                const nextZoom = event.deltaY < 0 ? current + ZOOM_STEP : current - ZOOM_STEP;
                return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(nextZoom.toFixed(2))));
            });
        };

        document.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            document.removeEventListener('wheel', handleWheel);
        };
    }, [isOpen]);

    if (!isOpen || !imageUrl) return null;

    const zoomIn = () => {
        setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
    };

    const zoomOut = () => {
        setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
    };

    const resetZoom = () => {
        setZoom(1);
    };

    const handleImageDoubleClick = () => {
        setZoom((current) => current === 1 ? 2 : 1);
    };

    return (
        <div
            onClick={onClose}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm sm:p-5"
        >
            <div
                className="relative flex h-full max-h-[95vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-lg bg-black shadow-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-black/80 px-3 py-2.5 sm:px-4">
                    <div className="min-w-0">
                        <p
                            className="truncate text-[11px] font-medium text-white sm:text-xs"
                            title={fileName}
                        >
                            {fileName}
                        </p>

                        <p className="mt-0.5 text-[9px] text-white/40">
                            {Math.round(zoom * 100)}%
                        </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                        <button
                            type="button"
                            onClick={zoomOut}
                            disabled={zoom <= MIN_ZOOM}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                            title="Zoom out (-)"
                            aria-label="Zoom out"
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
                                <path d="M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={resetZoom}
                            className="hidden min-w-[48px] rounded-md px-2 py-1.5 text-[10px] font-medium text-white/70 transition hover:bg-white/10 hover:text-white sm:block"
                            title="Reset zoom (0)"
                        >
                            {Math.round(zoom * 100)}%
                        </button>

                        <button
                            type="button"
                            onClick={zoomIn}
                            disabled={zoom >= MAX_ZOOM}
                            className="flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                            title="Zoom in (+)"
                            aria-label="Zoom in"
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={resetZoom}
                            className="hidden h-8 items-center justify-center rounded-md px-2 text-[9px] font-medium text-white/60 transition hover:bg-white/10 hover:text-white sm:flex"
                            title="Reset zoom (0)"
                        >
                            Reset
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
                            title="Close (Esc)"
                            aria-label="Close image preview"
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                                <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black">
                    <img
                        src={imageUrl}
                        alt={fileName ?? 'Image attachment'}
                        draggable={false}
                        onDoubleClick={handleImageDoubleClick}
                        className="max-h-full max-w-full select-none object-contain transition-transform duration-150 ease-out"
                        style={{ transform: `scale(${zoom})` }}
                    />

                    <div className="pointer-events-none absolute bottom-3 left-1/2 hidden -translate-x-1/2 rounded-full bg-black/60 px-3 py-1.5 text-[9px] text-white/50 backdrop-blur-sm sm:block">
                        Scroll to zoom · Double-click to toggle · Esc to close
                    </div>
                </div>
            </div>
        </div>
    );
}